import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthModule';
import { MailCheck, Trash2 } from 'lucide-react';
import { API } from './profileHelpers';
import ProfileNavTopBar from '../ProfileNavTopBar';
import { useT } from '../../i18n/I18nModule';
import { loadingPageText } from '../../i18n/loadingPage';
import { formatBotSenderDisplayName } from '../../i18n/botSender';

export default function PostilaatikkoPage() {
  const { user } = useAuth();
  const t = useT();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [markReadBusyId, setMarkReadBusyId] = useState(null);

  const fetchInbox = useCallback(() => {
    const userId = user?.id;
    if (!userId) return;
    const userIdStr = String(userId).replace(/n$/, '');
    setLoading(true);
    setError(null);
    fetch(`${API}/api/direct-messages/inbox/${encodeURIComponent(userIdStr)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (Array.isArray(data)) {
          setMessages(data);
        } else if (Array.isArray(data?.messages)) {
          setMessages(data.messages);
        } else {
          setMessages([]);
        }
      })
      .catch(() => {
        setError('Postilaatikon lataus epäonnistui.');
        setMessages([]);
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    fetchInbox();
  }, [user?.id, fetchInbox]);

  const handleClearInbox = useCallback(() => {
    if (!window.confirm('Haluatko varmasti tyhjentää koko postilaatikon? Kaikki viestit poistetaan pysyvästi.')) return;
    const userIdStr = user?.id != null ? String(user.id).replace(/n$/, '') : '';
    if (!userIdStr) return;
    fetch(`${API}/api/direct-messages/inbox/${encodeURIComponent(userIdStr)}`, { method: 'DELETE' })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then(() => {
        setMessages([]);
      })
      .catch(() => {
        setError('Postilaatikon tyhjennys epäonnistui.');
      });
  }, [user?.id]);

  const handleMarkRead = useCallback(
    (e, messageId, alreadyRead) => {
      e.stopPropagation();
      if (alreadyRead || messageId == null) return;
      const userIdStr = user?.id != null ? String(user.id).replace(/n$/, '') : '';
      if (!userIdStr) return;
      setMarkReadBusyId(messageId);
      fetch(`${API}/api/direct-messages/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId: String(messageId), userId: userIdStr }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject()))
        .then(() => {
          setMessages((prev) =>
            prev.map((row) =>
              String(row.id) === String(messageId)
                ? { ...row, messageRead: true }
                : row,
            ),
          );
        })
        .catch(() => {
          setError('Merkitseminen luetuksi epäonnistui.');
        })
        .finally(() => setMarkReadBusyId(null));
    },
    [user?.id],
  );

  if (!user) {
    return (
      <div className="App-block">
        <p>Kirjaudu sisään nähdäksesi postilaatikkosi.</p>
      </div>
    );
  }

  return (
    <div className="App-block">
      <ProfileNavTopBar user={user} currentPage="inbox" />
      <div className="Inbox-header-with-trash">
        {messages.length > 0 && (
          <button
            type="button"
            className="Inbox-trash-button"
            onClick={handleClearInbox}
            aria-label="Tyhjennä postilaatikko"
            title="Tyhjennä postilaatikko"
          >
            <Trash2 size={20} aria-hidden />
          </button>
        )}
        <h2 className="Inbox-page-title">{t('messages.inboxTitle')}</h2>
      </div>
      {loading ? (
        <p>{loadingPageText(t, 'inbox')}</p>
      ) : error ? (
        <p className="Inbox-error">{error}</p>
      ) : messages.length === 0 ? (
        <p>Ei viestejä.</p>
      ) : (
        <div className="Inbox-table">
          <div className="Inbox-header-row">
            <div className="Inbox-col-from">{t('messages.from')}</div>
            <div className="Inbox-col-subject">{t('messages.subject')}</div>
            <div className="Inbox-col-time">{t('messages.time')}</div>
            <div className="Inbox-col-actions" aria-hidden />
          </div>
          <div className="Inbox-rows">
            {messages.map((m) => {
              const from = formatBotSenderDisplayName(
                {
                  senderKind: m.senderKind,
                  fromUserId: m.fromUserId,
                  fromDisplayName: m.fromDisplayName,
                  fromUsername: m.fromUsername,
                },
                t,
              );
              const created =
                m.createdAt != null ? new Date(m.createdAt).toLocaleString('fi-FI') : '';
              const unread = m.messageRead === false;
              const busy = markReadBusyId != null && String(markReadBusyId) === String(m.id);
              const originalPostRaw =
                m.originalPostId != null && String(m.originalPostId).trim() !== ''
                  ? String(m.originalPostId).replace(/n$/, '')
                  : m.threadRootId != null && String(m.threadRootId).trim() !== ''
                    ? String(m.threadRootId).replace(/n$/, '')
                    : '';
              const threadMailId =
                originalPostRaw && /^\d+$/.test(originalPostRaw) ? originalPostRaw : null;
              const openMailId =
                threadMailId ||
                (m.id != null ? String(m.id).replace(/n$/, '') : '');
              const subjectHrefId = threadMailId || openMailId;
              let subject = '';
              if (m.inboxSubject && String(m.inboxSubject).trim()) {
                subject = String(m.inboxSubject).trim();
              } else if (m.content && typeof m.content === 'string') {
                const words = m.content.trim().split(/\s+/);
                subject = words.slice(0, 3).join(' ');
                if (words.length > 3) {
                  subject += '…';
                }
              }
              return (
                <div
                  key={m.id}
                  className={`Inbox-row${unread ? ' Inbox-row--unread' : ''}`}
                  onClick={() => {
                    if (openMailId && typeof window !== 'undefined') {
                      window.location.href = `/mail/${openMailId}`;
                    }
                  }}
                >
                  <div className="Inbox-col-from">{from}</div>
                  <div className="Inbox-col-subject">
                    {subject && subjectHrefId ? (
                      <a
                        className="Inbox-subject-link"
                        href={`/mail/${subjectHrefId}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {subject}
                      </a>
                    ) : (
                      subject || '—'
                    )}
                  </div>
                  <div className="Inbox-col-time">{created}</div>
                  <div className="Inbox-col-actions">
                    <button
                      type="button"
                      className="Inbox-mark-read-button"
                      disabled={!unread || busy}
                      aria-label={unread ? t('messages.unread') : t('messages.read')}
                      title={unread ? t('messages.unread') : t('messages.read')}
                      onClick={(e) => handleMarkRead(e, m.id, !unread)}
                    >
                      <MailCheck size={18} aria-hidden />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

